/* W8.9 — Outcome Learning : prédit vs réalisé. Doctrine : qualité de la décision
 * ≠ qualité du résultat. Agrégats seulement si l'échantillon suffit. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { GraduationCap, Loader2 } from "lucide-react";

const fmtMoney = (v, cur = "CHF") => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
const fmtCHF = (v) => fmtMoney(v, "CHF");
const ALIGN_LABEL = { alignee_ev: ["Alignée EV", "text-[#1D9E75] border-[#1D9E75]/40"], contre_ev: ["Contre EV", "text-[#EF9F27] border-[#EF9F27]/40"], neutre: ["Neutre", "text-zinc-400 border-white/15"] };
const SIGN_LABEL = { positif: ["Résultat +", "text-[#1D9E75] border-[#1D9E75]/40"], negatif: ["Résultat −", "text-[#E24B4A] border-[#E24B4A]/40"], nul: ["Résultat 0", "text-zinc-400 border-white/15"] };

export function OutcomeLearning() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/workspace/decisions/learning").then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e))); }, []);
  if (!data) return <div className="flex justify-center py-16 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const agg = data.aggregates || {};
  return (
    <div className="space-y-3" data-testid="outcome-learning">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02] flex items-center gap-1.5">
        <GraduationCap className="w-3.5 h-3.5 text-[#7F77DD]" /> {data.doctrine}
      </div>
      {data.snapshot_note && <div className="text-[10px] text-zinc-500 px-1" data-testid="learning-snapshot-note">{data.snapshot_note}</div>}
      <div className="noam-card p-3 flex gap-4 text-xs text-zinc-400 flex-wrap" data-testid="learning-aggregates">
        <span>Décisions résolues <b className="text-zinc-200">{data.total_closed}</b></span>
        {agg.status === "INSUFFICIENT_DATA" ? (
          <span className="px-2 py-0.5 rounded border border-[#EF9F27]/40 text-[#EF9F27]" data-testid="learning-insufficient">
            INSUFFICIENT_DATA — {agg.scored}/{agg.min_sample} outcomes chiffrés : aucune conclusion statistique tant que l'échantillon est insuffisant
          </span>
        ) : (
          <>
            <span>Surprise moyenne <b className="text-zinc-200">{fmtCHF(agg.mean_surprise_chf)}</b></span>
            <span>Taux résultats positifs <b className="text-zinc-200">{Math.round((agg.positive_outcome_rate || 0) * 100)}%</b></span>
            <span>Taux d'alignement EV <b className="text-zinc-200">{Math.round((agg.ev_alignment_rate || 0) * 100)}%</b></span>
          </>
        )}
      </div>
      {data.rows.length === 0 && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="learning-empty">
          Aucune décision résolue : la boucle d'apprentissage commence quand des outcomes réels sont enregistrés.
        </div>
      )}
      <div className="space-y-2">
        {data.rows.map((r) => (
          <div key={r.id} className="noam-card p-3 space-y-1.5" data-testid={`learning-row-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="font-medium text-zinc-100 flex-1 truncate">{r.title}</span>
              <span className="text-[9px] text-zinc-600">{r.system} · v{r.estimation_version}</span>
              {r.verdict && <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-300">{r.verdict}</span>}
              {r.ev_alignment && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${ALIGN_LABEL[r.ev_alignment][1]}`}>{ALIGN_LABEL[r.ev_alignment][0]}</span>}
              {r.outcome_sign && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${SIGN_LABEL[r.outcome_sign][1]}`}>{SIGN_LABEL[r.outcome_sign][0]}</span>}
            </div>
            <div className="flex gap-4 text-[10px] text-zinc-400 flex-wrap">
              <span>Prédit (à la décision, v1) : EV <b className="text-zinc-200">{fmtMoney(r.predicted.ev_chf, r.currency)}</b></span>
              <span>AdjEV <b className="text-zinc-200">{fmtMoney(r.predicted.adjusted_ev_chf, r.currency)}</b></span>
              <span>P(succès) <b className="text-zinc-200">{r.predicted.p_success != null ? Math.round(r.predicted.p_success * 100) + "%" : "—"}</b></span>
              {r.predicted.prob_of_realized != null && <span>P(scénario réalisé) <b className="text-zinc-200">{Math.round(r.predicted.prob_of_realized * 100)}%</b></span>}
              <span>Réalisé : <b className={r.realized.payoff_chf > 0 ? "text-[#1D9E75]" : r.realized.payoff_chf < 0 ? "text-[#E24B4A]" : "text-zinc-200"}>{fmtMoney(r.realized.payoff_chf, r.currency)}</b>{r.realized.scenario_label ? ` (${r.realized.scenario_label})` : ""}</span>
              {r.surprise_chf != null && <span>Surprise vs estimation initiale <b className={r.surprise_chf >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{r.surprise_chf >= 0 ? "+" : ""}{fmtMoney(r.surprise_chf, r.currency)}</b></span>}
            </div>
            {r.reading && <div className="text-[10px] text-zinc-500 italic" data-testid={`learning-reading-${r.id}`}>{r.reading}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
